import React, { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface TeslaButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  icon?: ReactNode;
  iconRight?: boolean;
  loading?: boolean;
  className?: string;
  children: ReactNode;
  to?: string; // Add 'to' prop for React Router Link
}

/**
 * Tesla-inspired minimalist button component
 * Can be used as a regular button or a React Router Link
 */
const TeslaButton: React.FC<TeslaButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  iconRight = false,
  loading = false,
  className = '',
  children,
  to,
  ...props
}) => {
  // Determine button classes based on props
  const buttonClasses = [
    variant === 'primary' ? 'btn--primary' : 
    variant === 'secondary' ? 'btn--secondary' : 'btn--outline',
    size === 'sm' ? 'btn--sm' : 
    size === 'lg' ? 'btn--lg' : '',
    fullWidth ? 'btn--full' : '',
    icon ? 'btn--icon' : '',
    iconRight ? 'icon-right' : '',
    className
  ].filter(Boolean).join(' ');

  // Content to display regardless of button or link
  const content = loading ? (
    <div className="btn-loader">
      <span className="btn-text">{children}</span>
      <span className="loader"></span>
    </div>
  ) : (
    <>
      {icon && !iconRight && <span className="btn-icon">{icon}</span>}
      <span>{children}</span>
      {icon && iconRight && <span className="btn-icon">{icon}</span>}
    </>
  );

  // If 'to' prop is provided, render as Link
  if (to) {
    return (
      <Link 
        to={to} 
        className={`btn ${buttonClasses}`}
        style={{ textDecoration: 'none', display: 'inline-flex' }}
      >
        {content}
      </Link>
    );
  }

  // Otherwise render as regular button
  return (
    <button 
      className={`btn ${buttonClasses}`} 
      disabled={props.disabled || loading}
      {...props}
    >
      {content}
    </button>
  );
};

export default TeslaButton;